/**
 * In-memory cache utility for API services
 */

// Cache item interface
export interface CacheItem<T> {
  data: T;
  expiry: number;
  refreshing?: boolean;
  lastRefreshed?: number;
}

export class CacheService {
  // In-memory cache
  private static cache: Record<string, CacheItem<any>> = {};

  // Default cache duration (5 minutes in milliseconds)
  private static DEFAULT_CACHE_DURATION = 5 * 60 * 1000;
  
  // Refresh threshold (when to start refreshing before expiry, default 1 minute)
  private static REFRESH_THRESHOLD = 60 * 1000;
  
  // Refresh callbacks for auto-refreshing cache items
  private static refreshCallbacks: Record<string, () => Promise<any>> = {};

  /**
   * Cache'den veri alır
   * @param key Cache anahtarı
   * @returns Cache'deki veri veya null
   */
  static getFromCache<T>(key: string): T | null {
    const item = this.cache[key];

    if (process.env.NODE_ENV === 'development') {
      console.log('Cache Service: cache skipped:"' + key + '"');
      return null;
    }

    // Cache item yoksa veya süresi dolmuşsa null döndür
    if (!item || Date.now() > item.expiry) {
      if (item) {
        // Süresi dolmuş cache öğesini temizle
        delete this.cache[key];
        
        // Eğer bu anahtar için bir refresh callback varsa çağır
        this.triggerRefresh(key);
      }
      return null;
    }
    
    // Eğer cache süresi dolmak üzereyse ve yenilenmiyor durumdaysa, arka planda yenile
    const timeToExpiry = item.expiry - Date.now();
    if (timeToExpiry < this.REFRESH_THRESHOLD && !item.refreshing) {
      this.triggerRefresh(key);
    }

    return item.data as T;
  }

  /**
   * Veriyi cache'e ekler
   * @param key Cache anahtarı
   * @param data Cache'e eklenecek veri
   * @param duration Cache süresi (milisaniye cinsinden)
   */
  static addToCache<T>(key: string, data: T, duration: number = this.DEFAULT_CACHE_DURATION): void {
    this.cache[key] = {
      data,
      expiry: Date.now() + duration,
      lastRefreshed: Date.now(),
      refreshing: false
    };
  }

  /**
   * Cache'i temizler
   * @param key Temizlenecek cache anahtarı (boş ise tüm cache temizlenir)
   */
  static clearCache(key?: string): void {
    if (key) {
      delete this.cache[key];
      // Refresh callback'i de temizle
      delete this.refreshCallbacks[key];
    } else {
      this.cache = {};
      this.refreshCallbacks = {};
    }
  }
  
  /**
   * Cache için otomatik yenileme callback'i ekler
   * @param key Cache anahtarı
   * @param callback Yenileme işlemini gerçekleştirecek fonksiyon
   */
  static registerRefreshCallback(key: string, callback: () => Promise<any>): void {
    this.refreshCallbacks[key] = callback;
  }
  
  /**
   * Cache yenileme işlemini tetikler
   * @param key Cache anahtarı
   */
  private static async triggerRefresh(key: string): Promise<void> {
    const callback = this.refreshCallbacks[key];
    if (!callback) return;
    
    // Eğer cache öğesi varsa, refreshing flag'ini true yap
    if (this.cache[key]) {
      this.cache[key].refreshing = true;
    }
    
    try {
      // Callback'i çağır, sonucu bekleme (arka planda çalışacak)
      callback().catch(error => {
        console.error(`Cache refresh error for key "${key}":`, error);
      });
    } catch (error) {
      console.error(`Error triggering cache refresh for key "${key}":`, error);
    }
  }
}
